/**
 * Local-first persistence (localStorage). Matches the Supabase schema in schema.sql.
 * When REACT_APP_SUPABASE_* is set, you can extend this module to read/write via `supabase`.
 */
import type {
  ChecklistInstance,
  ChecklistItem,
  ChecklistKind,
  ChecklistTemplate,
  Receipt,
  ReceiptCategory,
  RouteMode,
  Trip,
  TripRoute,
} from '../types';
import {
  extractHotelNameHints,
  extractTransportLegsFromDocument,
  legFingerprint,
  lodgingReceiptCoversHotel,
  routeRecordFingerprint,
} from './travelDocumentExtraction';
import { receiptAmountEurForSummary } from './receiptAmounts';
import { analyzeReceiptFile, readFileAsDataUrlIfSmall } from './receiptAnalysis';
import { compressImageFileForStorage } from './receiptImageCompress';
import {
  clearAllReceiptBlobs,
  deleteReceiptBlob,
  getReceiptBlob,
  putReceiptBlob,
} from './receiptBlobStore';

const STORAGE_KEY = 'travel_buddy_v1_db';

/** Default to-do template items (new trips get this list automatically). */
export const DEFAULT_TRIP_TODO_ITEMS: string[] = [
  'Event registration',
  'Order taxi or airport ride',
  'Online check-in (flight)',
  'Print or download travel documents',
  'Confirm accommodation',
  'Travel insurance — verify coverage',
  'Set calendar / phone reminders',
  'Charge devices; pack adapters',
  'Check passport / ID validity',
];

const DEFAULT_TODO_TEMPLATE_NAME = 'Trip prep & logistics';

interface Database {
  trips: Trip[];
  receipts: Receipt[];
  tripRoutes: TripRoute[];
  checklistTemplates: ChecklistTemplate[];
  checklistInstances: ChecklistInstance[];
  checklistItems: ChecklistItem[];
}

const ALL_RECEIPT_CATEGORIES: ReceiptCategory[] = [
  'food',
  'transport',
  'lodging',
  'flight',
  'train',
  'bus',
  'ferry',
  'other',
];

function normalizeDb(db: Database): Database {
  if (!Array.isArray(db.tripRoutes)) {
    db.tripRoutes = [];
  }
  for (const r of db.receipts) {
    if (!ALL_RECEIPT_CATEGORIES.includes(r.category)) {
      r.category = 'other';
    }
    if (r.include_in_receipts_pdf === undefined) r.include_in_receipts_pdf = true;
    if (r.include_in_summary === undefined) r.include_in_summary = true;
  }
  for (const t of db.checklistTemplates) {
    if (t.kind !== 'todo' && t.kind !== 'packing') t.kind = 'packing';
  }
  for (const i of db.checklistInstances) {
    if (i.kind !== 'todo' && i.kind !== 'packing') i.kind = 'packing';
  }
  return db;
}

function ensureDefaultTodoTemplate(db: Database): boolean {
  const has = db.checklistTemplates.some(
    (t) => t.kind === 'todo' && t.name === DEFAULT_TODO_TEMPLATE_NAME
  );
  if (has) return false;
  db.checklistTemplates.push({
    id: genId(),
    name: DEFAULT_TODO_TEMPLATE_NAME,
    kind: 'todo',
    items: [...DEFAULT_TRIP_TODO_ITEMS],
  });
  return true;
}

function nowIso(): string {
  return new Date().toISOString();
}

function genId(): string {
  return crypto.randomUUID();
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function seed(): Database {
  const t1 = genId();
  const t2 = genId();
  const t3 = genId();
  return {
    trips: [],
    receipts: [],
    tripRoutes: [],
    checklistTemplates: [
      {
        id: t1,
        name: 'Weekend trip',
        kind: 'packing',
        items: ['Toothbrush', 'Charger', 'Snacks', 'Water bottle'],
      },
      {
        id: t2,
        name: 'Business',
        kind: 'packing',
        items: ['Laptop', 'ID', 'Receipts folder', 'Business cards'],
      },
      {
        id: t3,
        name: DEFAULT_TODO_TEMPLATE_NAME,
        kind: 'todo',
        items: [...DEFAULT_TRIP_TODO_ITEMS],
      },
    ],
    checklistInstances: [],
    checklistItems: [],
  };
}

function load(): Database {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const db = seed();
      save(db);
      return db;
    }
    const parsed = JSON.parse(raw) as Database;
    const missingRoutes = !Array.isArray(parsed.tripRoutes);
    normalizeDb(parsed);
    const addedTodoTemplate = ensureDefaultTodoTemplate(parsed);
    if (missingRoutes || addedTodoTemplate) save(parsed);
    return parsed;
  } catch {
    const db = seed();
    save(db);
    return db;
  }
}

function save(db: Database): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(db));
}

/** Unpacked items first (lower sort_order), then packed — one contiguous order line */
function normalizeInstanceItems(db: Database, instanceId: string): void {
  const items = db.checklistItems.filter((i) => i.instance_id === instanceId);
  const unpacked = items.filter((i) => !i.is_packed).sort((a, b) => a.sort_order - b.sort_order);
  const packed = items.filter((i) => i.is_packed).sort((a, b) => a.sort_order - b.sort_order);
  let o = 0;
  unpacked.forEach((it) => {
    it.sort_order = o++;
  });
  packed.forEach((it) => {
    it.sort_order = o++;
  });
}

export async function listTrips(): Promise<Trip[]> {
  const db = load();
  return [...db.trips].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
}

export async function getTrip(id: string): Promise<Trip | null> {
  return load().trips.find((t) => t.id === id) ?? null;
}

/** Deletes one trip and all related receipts (and blobs), routes, and packing lists. Templates are kept. */
export async function deleteTrip(tripId: string): Promise<void> {
  const db = load();
  if (!db.trips.some((t) => t.id === tripId)) {
    throw new Error('Trip not found');
  }

  const receiptIds = db.receipts.filter((r) => r.trip_id === tripId).map((r) => r.id);
  for (const id of receiptIds) {
    try {
      await deleteReceiptBlob(id);
    } catch {
      /* ignore */
    }
  }

  db.receipts = db.receipts.filter((r) => r.trip_id !== tripId);
  db.tripRoutes = db.tripRoutes.filter((r) => r.trip_id !== tripId);

  const instIds = db.checklistInstances.filter((i) => i.trip_id === tripId).map((i) => i.id);
  for (const iid of instIds) {
    db.checklistItems = db.checklistItems.filter((it) => it.instance_id !== iid);
  }
  db.checklistInstances = db.checklistInstances.filter((i) => i.trip_id !== tripId);

  db.trips = db.trips.filter((t) => t.id !== tripId);
  save(db);
}

export async function createTrip(input: { name: string }): Promise<Trip> {
  const db = load();
  const trip: Trip = {
    id: genId(),
    name: input.name.trim() || 'Untitled trip',
    start_date: null,
    end_date: null,
    is_generic: false,
    created_at: nowIso(),
  };
  db.trips.push(trip);
  save(db);

  const todoTpl =
    db.checklistTemplates.find(
      (t) => t.kind === 'todo' && t.name === DEFAULT_TODO_TEMPLATE_NAME
    ) ?? db.checklistTemplates.find((t) => t.kind === 'todo');
  if (todoTpl) {
    try {
      await addChecklistFromTemplate(trip.id, todoTpl.id);
    } catch {
      /* ignore */
    }
  }

  return trip;
}

export async function listReceipts(tripId: string): Promise<Receipt[]> {
  return load()
    .receipts.filter((r) => r.trip_id === tripId)
    .sort((a, b) => (a.date < b.date ? 1 : -1));
}

export async function addReceiptFromFile(
  tripId: string,
  file: File,
  onProgress?: (msg: string) => void
): Promise<Receipt> {
  const db = load();
  const parsed = await analyzeReceiptFile(file, onProgress);
  const storedFile = await compressImageFileForStorage(file);
  const dataUrl = await readFileAsDataUrlIfSmall(storedFile);
  const mime =
    storedFile.type ||
    file.type ||
    (file.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : '');

  const receipt: Receipt = {
    id: genId(),
    trip_id: tripId,
    image_url: dataUrl,
    merchant: parsed.merchant,
    amount: parsed.amount,
    currency: parsed.currency,
    vat: null,
    category: parsed.category,
    date: parsed.date || todayIsoDate(),
    mime_type: mime || undefined,
    file_name: storedFile.name || file.name,
    amount_eur: null,
    include_in_receipts_pdf: true,
    include_in_summary: true,
    raw_text_excerpt: parsed.rawText ? parsed.rawText.slice(0, 600) : undefined,
    analysis_note: parsed.note,
    contact_emails: parsed.contact_emails.length ? parsed.contact_emails : undefined,
    contact_websites: parsed.contact_websites.length ? parsed.contact_websites : undefined,
  };
  db.receipts.push(receipt);
  save(db);
  try {
    await putReceiptBlob(receipt.id, storedFile);
  } catch {
    /* offline / private mode — export may fall back to image_url only */
  }
  try {
    await syncRoutesFromTravelDocument(tripId, receipt, parsed.rawText || '');
  } catch {
    /* extraction is best-effort */
  }
  return receipt;
}

/**
 * Re-run text extraction (including PDF OCR when needed) and add any newly detected routes.
 * Updates stored receipt excerpt/note from the fresh parse. Use after code improvements or for scanned PDFs.
 */
export async function resyncRoutesFromStoredReceipt(
  tripId: string,
  receiptId: string,
  onProgress?: (msg: string) => void
): Promise<void> {
  const db = load();
  const receipt = db.receipts.find((r) => r.id === receiptId && r.trip_id === tripId);
  if (!receipt) throw new Error('Receipt not found');

  const blob = await getReceiptBlob(receiptId);
  if (!blob || blob.size === 0) {
    throw new Error('No saved file in this browser — upload the receipt again');
  }

  const name = receipt.file_name || 'receipt';
  const mime = receipt.mime_type || blob.type || '';
  const file = new File([blob], name, { type: mime || 'application/octet-stream' });

  const parsed = await analyzeReceiptFile(file, onProgress);
  receipt.raw_text_excerpt = parsed.rawText ? parsed.rawText.slice(0, 600) : undefined;
  receipt.analysis_note = parsed.note;
  receipt.contact_emails = parsed.contact_emails.length ? parsed.contact_emails : undefined;
  receipt.contact_websites = parsed.contact_websites.length ? parsed.contact_websites : undefined;
  save(db);

  try {
    await syncRoutesFromTravelDocument(tripId, receipt, parsed.rawText || '');
  } catch {
    throw new Error('Could not extract routes from text');
  }
}

/** Auto-add Routes from boarding passes & agent itineraries; skip duplicates and hotel lines covered by lodging receipts */
async function syncRoutesFromTravelDocument(
  tripId: string,
  receipt: Receipt,
  fullText: string
): Promise<void> {
  if (!fullText.trim()) return;

  const tripReceipts = load().receipts.filter((r) => r.trip_id === tripId);
  const lodgingReceipts = tripReceipts.filter((r) => r.category === 'lodging' && r.id !== receipt.id);

  for (const hint of extractHotelNameHints(fullText)) {
    if (lodgingReceiptCoversHotel(hint, lodgingReceipts)) {
      /* Itinerary mentions a hotel we already have as a lodging receipt — do not duplicate */
    }
  }

  const legs = extractTransportLegsFromDocument(fullText);
  if (legs.length === 0) return;

  const existing = load().tripRoutes.filter((r) => r.trip_id === tripId);
  const seen = new Set(existing.map((r) => routeRecordFingerprint(r)));

  for (const leg of legs) {
    const fp = legFingerprint(leg);
    if (seen.has(fp)) continue;

    const parts: string[] = [];
    if (leg.carrier) parts.push(leg.carrier);
    parts.push('From uploaded document');

    const route = await addRouteInternal(tripId, {
      mode: leg.mode,
      origin: leg.origin,
      destination: leg.destination,
      depart_at: leg.depart_at,
      arrive_at: leg.arrive_at,
      notes: parts.join(' · '),
      flight_number: leg.flight_number,
      source_receipt_id: receipt.id,
    });
    seen.add(routeRecordFingerprint(route));
  }
}

async function addRouteInternal(
  tripId: string,
  input: {
    mode: RouteMode;
    origin: string;
    destination: string;
    depart_at: string | null;
    arrive_at: string | null;
    notes: string;
    flight_number?: string | null;
    source_receipt_id?: string | null;
  }
): Promise<TripRoute> {
  const db = load();
  const route: TripRoute = {
    id: genId(),
    trip_id: tripId,
    mode: input.mode,
    origin: input.origin.trim(),
    destination: input.destination.trim(),
    depart_at: input.depart_at?.trim() || null,
    arrive_at: input.arrive_at?.trim() || null,
    notes: input.notes.trim(),
    created_at: nowIso(),
    flight_number: input.flight_number ?? null,
    source_receipt_id: input.source_receipt_id ?? null,
  };
  db.tripRoutes.push(route);
  save(db);
  return route;
}

export async function updateReceiptCategory(
  receiptId: string,
  category: ReceiptCategory
): Promise<Receipt | null> {
  const db = load();
  const r = db.receipts.find((x) => x.id === receiptId);
  if (!r) return null;
  r.category = ALL_RECEIPT_CATEGORIES.includes(category) ? category : 'other';
  save(db);
  return r;
}

export type ReceiptEditableFields = Partial<
  Pick<
    Receipt,
    | 'merchant'
    | 'file_name'
    | 'amount'
    | 'currency'
    | 'amount_eur'
    | 'include_in_receipts_pdf'
    | 'include_in_summary'
  >
>;

export async function updateReceipt(
  receiptId: string,
  patch: ReceiptEditableFields
): Promise<Receipt | null> {
  const db = load();
  const r = db.receipts.find((x) => x.id === receiptId);
  if (!r) return null;
  if (patch.merchant !== undefined) {
    r.merchant = String(patch.merchant).trim() || '';
  }
  if (patch.file_name !== undefined) {
    r.file_name = patch.file_name.trim() || undefined;
  }
  if (patch.amount !== undefined) {
    r.amount = String(patch.amount).trim() || '0';
  }
  if (patch.currency !== undefined) {
    r.currency = String(patch.currency).trim().toUpperCase().slice(0, 8) || 'EUR';
  }
  if (patch.amount_eur !== undefined) {
    const s = patch.amount_eur == null ? '' : String(patch.amount_eur).trim();
    r.amount_eur = s === '' ? null : s;
  }
  if (patch.include_in_receipts_pdf !== undefined) {
    r.include_in_receipts_pdf = Boolean(patch.include_in_receipts_pdf);
  }
  if (patch.include_in_summary !== undefined) {
    r.include_in_summary = Boolean(patch.include_in_summary);
  }
  save(db);
  return r;
}

export async function removeReceipt(receiptId: string): Promise<void> {
  try {
    await deleteReceiptBlob(receiptId);
  } catch {
    /* ignore */
  }
  const db = load();
  db.receipts = db.receipts.filter((r) => r.id !== receiptId);
  db.tripRoutes = db.tripRoutes.filter((r) => r.source_receipt_id !== receiptId);
  save(db);
}

export async function listRoutes(tripId: string): Promise<TripRoute[]> {
  return load()
    .tripRoutes.filter((r) => r.trip_id === tripId)
    .sort((a, b) => {
      const ta = a.depart_at || a.created_at;
      const tb = b.depart_at || b.created_at;
      return ta.localeCompare(tb);
    });
}

export async function addRoute(
  tripId: string,
  input: {
    mode: RouteMode;
    origin: string;
    destination: string;
    depart_at: string | null;
    arrive_at: string | null;
    notes: string;
    flight_number?: string | null;
    source_receipt_id?: string | null;
  }
): Promise<TripRoute> {
  return addRouteInternal(tripId, input);
}

export async function updateRoute(
  routeId: string,
  patch: Partial<
    Pick<
      TripRoute,
      'mode' | 'origin' | 'destination' | 'depart_at' | 'arrive_at' | 'notes' | 'flight_number'
    >
  >
): Promise<TripRoute | null> {
  const db = load();
  const route = db.tripRoutes.find((r) => r.id === routeId);
  if (!route) return null;
  if (patch.mode !== undefined) route.mode = patch.mode;
  if (patch.origin !== undefined) route.origin = patch.origin.trim();
  if (patch.destination !== undefined) route.destination = patch.destination.trim();
  if (patch.depart_at !== undefined) route.depart_at = patch.depart_at?.trim() || null;
  if (patch.arrive_at !== undefined) route.arrive_at = patch.arrive_at?.trim() || null;
  if (patch.notes !== undefined) route.notes = patch.notes.trim();
  if (patch.flight_number !== undefined) route.flight_number = patch.flight_number?.trim() || null;
  save(db);
  return route;
}

export async function removeRoute(routeId: string): Promise<void> {
  const db = load();
  db.tripRoutes = db.tripRoutes.filter((r) => r.id !== routeId);
  save(db);
}

export async function listTemplates(kind?: ChecklistKind): Promise<ChecklistTemplate[]> {
  let list = [...load().checklistTemplates];
  if (kind) {
    list = list.filter((t) => (t.kind ?? 'packing') === kind);
  }
  return list.sort((a, b) => a.name.localeCompare(b.name));
}

export async function createTemplate(input: {
  name: string;
  items: string[];
  kind?: ChecklistKind;
}): Promise<ChecklistTemplate> {
  const db = load();
  const t: ChecklistTemplate = {
    id: genId(),
    name: input.name.trim() || 'Untitled template',
    items: input.items.map((s) => s.trim()).filter(Boolean),
    kind: input.kind === 'todo' ? 'todo' : 'packing',
  };
  db.checklistTemplates.push(t);
  save(db);
  return t;
}

export async function deleteTemplate(templateId: string): Promise<void> {
  const db = load();
  db.checklistTemplates = db.checklistTemplates.filter((t) => t.id !== templateId);
  save(db);
}

export async function listInstancesForTrip(
  tripId: string,
  kind?: ChecklistKind
): Promise<ChecklistInstance[]> {
  let list = load().checklistInstances.filter((i) => i.trip_id === tripId);
  if (kind) {
    list = list.filter((i) => (i.kind ?? 'packing') === kind);
  }
  return list.sort((a, b) => (a.created_at < b.created_at ? -1 : 1));
}

export async function listItemsForInstance(instanceId: string): Promise<ChecklistItem[]> {
  const items = load().checklistItems.filter((i) => i.instance_id === instanceId);
  return [...items].sort((a, b) => a.sort_order - b.sort_order);
}

export async function addChecklistFromTemplate(
  tripId: string,
  templateId: string
): Promise<{ instance: ChecklistInstance; items: ChecklistItem[] }> {
  const db = load();
  const tmpl = db.checklistTemplates.find((t) => t.id === templateId);
  if (!tmpl) throw new Error('Template not found');

  const instance: ChecklistInstance = {
    id: genId(),
    trip_id: tripId,
    name: tmpl.name,
    created_at: nowIso(),
    kind: tmpl.kind ?? 'packing',
  };
  db.checklistInstances.push(instance);

  const items: ChecklistItem[] = tmpl.items.map((label, order) => ({
    id: genId(),
    instance_id: instance.id,
    label,
    is_packed: false,
    sort_order: order,
  }));
  db.checklistItems.push(...items);
  normalizeInstanceItems(db, instance.id);
  save(db);
  return { instance, items: await listItemsForInstance(instance.id) };
}

export async function setItemPacked(itemId: string, is_packed: boolean): Promise<ChecklistItem> {
  const db = load();
  const item = db.checklistItems.find((i) => i.id === itemId);
  if (!item) throw new Error('Item not found');
  item.is_packed = is_packed;
  normalizeInstanceItems(db, item.instance_id);
  save(db);
  return item;
}

export async function toggleItemPacked(itemId: string): Promise<ChecklistItem> {
  const db = load();
  const item = db.checklistItems.find((i) => i.id === itemId);
  if (!item) throw new Error('Item not found');
  item.is_packed = !item.is_packed;
  normalizeInstanceItems(db, item.instance_id);
  save(db);
  return item;
}

export async function addChecklistItem(instanceId: string, label: string): Promise<ChecklistItem> {
  const db = load();
  const trimmed = label.trim();
  if (!trimmed) throw new Error('Label required');
  const inst = db.checklistInstances.find((i) => i.id === instanceId);
  if (!inst) throw new Error('List not found');
  const item: ChecklistItem = {
    id: genId(),
    instance_id: instanceId,
    label: trimmed,
    is_packed: false,
    sort_order: 0,
  };
  db.checklistItems.push(item);
  normalizeInstanceItems(db, instanceId);
  save(db);
  return db.checklistItems.find((i) => i.id === item.id)!;
}

export async function removeChecklistItem(itemId: string): Promise<void> {
  const db = load();
  const item = db.checklistItems.find((i) => i.id === itemId);
  if (!item) throw new Error('Item not found');
  const instanceId = item.instance_id;
  db.checklistItems = db.checklistItems.filter((i) => i.id !== itemId);
  normalizeInstanceItems(db, instanceId);
  save(db);
}

/** Reorder only unpacked items; packed stay at the bottom */
export async function reorderChecklistItems(
  instanceId: string,
  orderedUnpackedIds: string[]
): Promise<ChecklistItem[]> {
  const db = load();
  const all = db.checklistItems.filter((i) => i.instance_id === instanceId);
  const unpacked = all.filter((i) => !i.is_packed);
  const unpackedSet = new Set(unpacked.map((i) => i.id));
  if (orderedUnpackedIds.length !== unpacked.length) {
    throw new Error('Invalid reorder: must include all unpacked items');
  }
  if (orderedUnpackedIds.some((id) => !unpackedSet.has(id))) {
    throw new Error('Invalid reorder: unknown id');
  }
  orderedUnpackedIds.forEach((id, idx) => {
    const it = db.checklistItems.find((x) => x.id === id);
    if (it) it.sort_order = idx;
  });
  normalizeInstanceItems(db, instanceId);
  save(db);
  return listItemsForInstance(instanceId);
}

export async function spendSummary(tripId: string): Promise<{
  byCategory: Record<ReceiptCategory, number>;
  total: number;
  currency: string;
}> {
  const receipts = (await listReceipts(tripId)).filter((r) => r.include_in_summary !== false);
  const byCategory: Record<ReceiptCategory, number> = {
    food: 0,
    transport: 0,
    lodging: 0,
    flight: 0,
    train: 0,
    bus: 0,
    ferry: 0,
    other: 0,
  };
  const currency = 'EUR';
  for (const r of receipts) {
    const n = receiptAmountEurForSummary(r);
    byCategory[r.category] = (byCategory[r.category] ?? 0) + n;
  }
  const total = Object.values(byCategory).reduce((a, b) => a + b, 0);
  return { byCategory, total, currency };
}

export async function exportTripJson(tripId: string): Promise<unknown> {
  const trip = await getTrip(tripId);
  if (!trip) throw new Error('Trip not found');
  const receipts = await listReceipts(tripId);
  const instances = await listInstancesForTrip(tripId);
  const packing = await Promise.all(
    instances.map(async (inst) => ({
      ...inst,
      items: await listItemsForInstance(inst.id),
    }))
  );
  const summary = await spendSummary(tripId);
  const routes = await listRoutes(tripId);
  return { trip, receipts, packing, routes, summary };
}

export async function clearAllLocalData(): Promise<void> {
  try {
    await clearAllReceiptBlobs();
  } catch {
    /* ignore */
  }
  localStorage.removeItem(STORAGE_KEY);
  const db = seed();
  save(db);
}
